import { QueryClient } from '@tanstack/react-query';

/** Filtre verileri 30 dakika cache'de kalır - arama sırasında yeniden çekilmez */
const FILTER_STALE_TIME = 1000 * 60 * 30; // 30 dakika

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: FILTER_STALE_TIME,
      gcTime: FILTER_STALE_TIME * 2,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export const filterQueryKeys = {
  mentorSearch: ['filters', 'mentor-search'] as const,
  bootcamp: ['filters', 'bootcamp'] as const,
  jobPosting: ['filters', 'job-posting'] as const,
  menteeSearch: ['filters', 'mentee-search'] as const,
};

export const notificationQueryKeys = {
  list: (page: number, pageSize: number) => ['notifications', page, pageSize] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
};

export const homeQueryKeys = {
  popularCategories: ['home', 'popular-categories'] as const,
  popularMentors: ['home', 'popular-mentors'] as const,
};

export const mentorSearchQueryKeys = {
  search: (params: object) => ['mentor-search', params] as const,
  advertisements: (type: number) => ['advertisements', type] as const,
};

export const menteeSearchQueryKeys = {
  search: (params: object) => ['mentee-search', params] as const,
};

export const bootcampQueryKeys = {
  search: (params: object) => ['bootcamp-search', params] as const,
  participants: (bootcampId: number, params: object) => ['bootcamp-participants', bootcampId, params] as const,
  detail: (id: number) => ['bootcamp-detail', id] as const,
  applicationStatus: (id: number) => ['bootcamp-detail', id, 'application-status'] as const,
};

export const jobPostingQueryKeys = {
  search: (params: object) => ['job-posting-search', params] as const,
  myApplications: ['job-posting', 'my-applications'] as const,
  myInterviews: ['job-posting', 'my-interviews'] as const,
  myApplicationIds: ['job-posting', 'my-application-ids'] as const,
  detail: (id: number) => ['job-posting-detail', id] as const,
};

export const mentorDetailQueryKeys = {
  detail: (id: number) => ['mentor-detail', id] as const,
  skills: (id: number) => ['mentor-detail', id, 'skills'] as const,
  educations: (id: number) => ['mentor-detail', id, 'educations'] as const,
  workExperiences: (id: number) => ['mentor-detail', id, 'work-experiences'] as const,
  projects: (id: number) => ['mentor-detail', id, 'projects'] as const,
  certificates: (id: number) => ['mentor-detail', id, 'certificates'] as const,
  bootcamps: (id: number) => ['mentor-detail', id, 'bootcamps'] as const,
  services: (id: number) => ['mentor-detail', id, 'services'] as const,
  availability: (id: number) => ['mentor-detail', id, 'availability'] as const,
  publishedBootcamps: (id: number) => ['mentor-detail', id, 'published-bootcamps'] as const,
};

export const menteeDetailQueryKeys = {
  detail: (id: number) => ['mentee-detail', id] as const,
  skills: (id: number) => ['mentee-detail', id, 'skills'] as const,
  educations: (id: number) => ['mentee-detail', id, 'educations'] as const,
  workExperiences: (id: number) => ['mentee-detail', id, 'work-experiences'] as const,
  projects: (id: number) => ['mentee-detail', id, 'projects'] as const,
  certificates: (id: number) => ['mentee-detail', id, 'certificates'] as const,
};

export const mentorProfileQueryKeys = {
  profile: ['mentor-panel', 'profile'] as const,
  categories: ['mentor-panel', 'categories'] as const,
  educations: ['mentor-panel', 'educations'] as const,
  skills: ['mentor-panel', 'skills'] as const,
  workExperiences: ['mentor-panel', 'work-experiences'] as const,
  projects: ['mentor-panel', 'projects'] as const,
  certificates: ['mentor-panel', 'certificates'] as const,
  availability: ['mentor-panel', 'availability'] as const,
  services: ['mentor-panel', 'services'] as const,
  bootcamps: ['mentor-panel', 'bootcamps'] as const,
  bootcampApplications: (bootcampId: number) => ['mentor-panel', 'bootcamp-applications', bootcampId] as const,
  mentees: ['mentor-panel', 'mentees'] as const,
  advertisementSlots: ['mentor-panel', 'advertisement-slots'] as const,
  advertisementRequests: ['mentor-panel', 'advertisement-requests'] as const,
  subscription: ['mentor-panel', 'subscription'] as const,
  subscriptionHistory: ['mentor-panel', 'subscription-history'] as const,
  bookings: ['mentor-panel', 'bookings'] as const,
  mentorBootcampApplications: ['mentor-panel', 'mentor-bootcamp-applications'] as const,
  approvedApplications: ['mentor-panel', 'approved-applications'] as const,
  incomingReferenceRequests: ['mentor-panel', 'incoming-reference-requests'] as const,
  receivedReferences: ['mentor-panel', 'received-references'] as const,
  favouriteCandidates: ['mentor-panel', 'favourites', 'candidates'] as const,
  sentFavouriteRequests: ['mentor-panel', 'favourites', 'requests'] as const,
};

export const menteeProfileQueryKeys = {
  profile: ['mentee-panel', 'profile'] as const,
  categories: ['mentee-panel', 'categories'] as const,
  educations: ['mentee-panel', 'educations'] as const,
  skills: ['mentee-panel', 'skills'] as const,
  workExperiences: ['mentee-panel', 'work-experiences'] as const,
  projects: ['mentee-panel', 'projects'] as const,
  certificates: ['mentee-panel', 'certificates'] as const,
  subscription: ['mentee-panel', 'subscription'] as const,
  subscriptionHistory: ['mentee-panel', 'subscription-history'] as const,
  bookings: ['mentee-panel', 'bookings'] as const,
  bootcampApplications: (page: number, pageSize: number, search?: string, status?: string) =>
    ['mentee-panel', 'bootcamp-applications', page, pageSize, search, status] as const,
  approvedApplications: ['mentee-panel', 'approved-applications'] as const,
  mentorRatingApplications: ['mentee-panel', 'mentor-rating-applications'] as const,
  incomingReferenceRequests: ['mentee-panel', 'incoming-reference-requests'] as const,
  receivedReferences: ['mentee-panel', 'received-references'] as const,
  incomingFavouriteRequests: ['mentee-panel', 'favourites', 'incoming'] as const,
  referrerMentors: ['mentee-panel', 'favourites', 'referrers'] as const,
};

export const companyProfileQueryKeys = {
  profile: ['company-panel', 'profile'] as const,
  categories: ['company-panel', 'categories'] as const,
  jobPostings: (params?: object) => ['company-panel', 'job-postings', params ?? {}] as const,
  applications: (params?: object) => ['company-panel', 'applications', params ?? {}] as const,
  jobApplications: (jobPostingId: number) => ['company-panel', 'job-applications', jobPostingId] as const,
  interviews: ['company-panel', 'interviews'] as const,
  favorites: (params?: object) => ['company-panel', 'favorites', params ?? {}] as const,
  pool: (params?: object) => ['company-panel', 'pool', params ?? {}] as const,
  mentors: ['company-panel', 'mentors'] as const,
  subscription: ['company-panel', 'subscription'] as const,
  subscriptionHistory: ['company-panel', 'subscription-history'] as const,
};

export const adminQueryKeys = {
  dashboard: ['admin-panel', 'dashboard'] as const,
  stats: ['admin-panel', 'stats'] as const,
  recentMentees: (limit: number) => ['admin-panel', 'recent-mentees', limit] as const,
  recentMentors: (limit: number) => ['admin-panel', 'recent-mentors', limit] as const,
  mentees: (page: number, pageSize: number, sortBy?: string, sortOrder?: string, search?: string) => ['admin-panel', 'mentees', page, pageSize, sortBy, sortOrder, search] as const,
  mentors: (page: number, pageSize: number, sortBy?: string, sortOrder?: string, search?: string) => ['admin-panel', 'mentors', page, pageSize, sortBy, sortOrder, search] as const,
  categories: (page: number, pageSize: number) => ['admin-panel', 'categories', page, pageSize] as const,
  companies: (page: number, pageSize: number, firmStatus?: number, search?: string, sortBy?: string, sortOrder?: string) => ['admin-panel', 'companies', page, pageSize, firmStatus, search, sortBy, sortOrder] as const,
  subscriptions: (page: number, pageSize: number, userType?: string, paymentStatus?: string) =>
    ['admin-panel', 'subscriptions', page, pageSize, userType, paymentStatus] as const,
  subscriptionPricing: ['admin-panel', 'subscription-pricing'] as const,
  advertisementSlots: ['admin-panel', 'advertisement-slots'] as const,
  advertisementRequests: (status?: number) => ['admin-panel', 'advertisement-requests', status] as const,
};
